// src/components/auth/FirstLoginHandler.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import BusinessCentralService from '../../services/businessCentralService';
import logger from '../../utils/logger';

const FirstLoginHandler = () => {
  const { user, isPendingFirstLogin, updateFirstLoginStatus, getAccessToken } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState({});
  const navigate = useNavigate();
  
  // Debug function
  const logDebugInfo = (message, data = {}) => {
    logger.log(`FirstLoginHandler Debug - ${message}:`, data);
    setDebugInfo(prev => ({ ...prev, [message]: data }));
  };
  
  // If not pending first login, don't render anything
  if (!isPendingFirstLogin) {
    logDebugInfo('Not pending first login, not rendering');
    return null;
  }
  
  logDebugInfo('Rendering FirstLoginHandler', { user });
  
  const handleContinue = async () => {
    logDebugInfo('Continue button clicked');
    setIsProcessing(true);
    setError(null);
    
    try {
      // Create BC service
      const bcService = new BusinessCentralService(getAccessToken);
      
      // Update user status in BC
      if (user && user.id) {
        logDebugInfo('Updating user status', { userId: user.id });
        const result = await bcService.updateUserStatus(user.id, 'Active');
        logDebugInfo('Status update result', result);
        
        if (!result || !result.success) {
          throw new Error(result?.message || 'Failed to update user status');
        }
      } else {
        logDebugInfo('No user ID available', { user });
        logger.warn('No user ID available, cannot update status');
      }
      
      // Update status in context
      updateFirstLoginStatus();
      logDebugInfo('Status updated in context');
      
      // Navigate to portal dashboard
      navigate('/portal');
      logDebugInfo('Navigating to portal');
    } catch (error) {
      logger.error('Error updating first login status:', error);
      logDebugInfo('Error updating status', { message: error.message, details: error });
      setError('There was an error updating your account status. Please try again or contact support.');
    } finally {
      setIsProcessing(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full shadow-2xl">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Welcome!</h2>
        
        <p className="mb-4 text-gray-700">
          This is your first login to the Business Central Employee Portal. We need to set up a few things before you can access your information.
        </p>
        
        <div className="mt-6">
          <p className="text-sm text-gray-500 mb-4">By continuing, you agree to our terms of use and privacy policy.</p>
        </div>
        
        {error && (
          <div className="mt-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}
        
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleContinue}
            disabled={isProcessing}
            className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${isProcessing ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
          >
            {isProcessing ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : (
              'Continue to Portal'
            )}
          </button>
        </div>
        
        {/* Debug info in development mode */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-6 p-3 bg-gray-50 rounded text-xs">
            <h3 className="font-medium">Debug Info:</h3>
            <pre className="mt-1 overflow-auto max-h-20">{JSON.stringify(debugInfo, null, 2)}</pre>
            <p className="mt-1">User: {user ? `${user.name} (${user.id})` : 'No user'}</p>
            <p>Status: {isProcessing ? 'Processing' : 'Ready'}</p>
            <div className="mt-2">
              <button
                onClick={() => {
                  updateFirstLoginStatus();
                  navigate('/portal');
                }}
                className="px-2 py-1 bg-red-500 text-white text-xs rounded"
              >
                Force Proceed
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FirstLoginHandler;